import React, { useEffect, useState } from "react";
import {
    Box,
    Button,
    Grid2 as Grid,
    Link,
    TextField,
    Typography,
} from "@mui/material";
import { useWallet } from "@solana/wallet-adapter-react";
import { useWalletModal } from '@solana/wallet-adapter-react-ui';
import { toast } from "react-toastify";
import { checkSAValid, createTransaction, submitTransaction } from "../api/bot.api";
import { useDispatch, useSelector } from "react-redux";
import { MenuInterface, NftInterface } from "./types";
import { setSelectedMenuAction, setSnackMsgAction } from "../store/slice";
import { PublicKey, VersionedTransaction } from "@solana/web3.js";
import { decryptToMint, getConnection } from "../libs/utils";
import Section2 from "./Section2";
import NumberField from "./NumberField";

export default function HomeContainer() {
    const { connected, publicKey, sendTransaction } = useWallet();
    const { setVisible } = useWalletModal();
    const [signupAddress, setSignupAddress] = useState<string>("");
    const [amount, setAmount] = useState("");
    const [isEditable, setIsEditable] = useState<boolean>(true);
    const settings = useSelector((state: any) => state.settings);
    const profile = useSelector((state: any) => state.profile);
    const isInUS = useSelector((state: any) => state.isInUS);
    const [isCheckingSA, setIsCheckingSA] = useState<boolean>(false);
    const [isSAValid, setIsSAValid] = useState<boolean>(true);
    const [isSigning, setIsSigning] = useState<boolean>(false);
    const [errors, setErrors] = useState({signupAddress: "", amount: ""});
    const dispatch = useDispatch();

    useEffect(() => {
        dispatch(setSelectedMenuAction(MenuInterface.HOME));

        const urlParams = new URLSearchParams(window.location.search);
        const addressFromUrl = urlParams.get("a");
        async function checkValid() {
            setIsCheckingSA(true);
            const res = await checkSAValid({ signupAddress: addressFromUrl });
            setIsCheckingSA(false);
            setIsSAValid(res.success);
        }
        if (addressFromUrl) {
            setSignupAddress(addressFromUrl);
            setIsEditable(false);
            checkValid();
        }
    }, []);

    const handleSignUp = async () => {
        if (!connected || !publicKey) {
            setVisible(true);
            return;
        }
        if (isInUS && !profile.isAdmin) {
            toast.error("Only Non-US residents can signup");
            return;
        }
        const tempErrors = {...errors};
        // Proceed with the signup process
        if (!amount) {
            tempErrors.amount = "Please input amount in USDC";
        }else if (amount < settings?.minSignupAmount) {
            tempErrors.amount = `Amount must be at least ${settings?.minSignupAmount}`;
        }

        try {
            new PublicKey(signupAddress);
        } catch (error) {
            tempErrors.signupAddress = "Invalid signup address";
        }
        if(tempErrors.amount || tempErrors.signupAddress) {
            setErrors(tempErrors);
            return;
        }
        const decryptedNftMint = decryptToMint(new PublicKey(signupAddress));
        const userNfts = profile.nfts || [];
        const hasNftMint = userNfts.some(
            (nft: NftInterface) => nft.id === decryptedNftMint.toBase58()
        );

        if (hasNftMint) {
            toast.error("Not allowed to signup with your own signup address");
            return;
        }

        setIsSigning(true);
        const res = await createTransaction({
            signupAddress,
            amount,
            investorPublicKey: publicKey?.toBase58(),
        });
        setIsSigning(false);

        if (!res.success) {
            toast.error(res.msg);
            return;
        }
        const { signedTx, historyId } = res.data;
        // Deserialize the transaction
        const tx = VersionedTransaction.deserialize(
            new Uint8Array(Buffer.from(signedTx, "base64"))
        );
        // Sign the transaction
        if (!sendTransaction) {
            toast.error("Failed to send transaction");
            return;
        }
        const connection = getConnection();
        const signature = await sendTransaction(tx, connection);
        if (!signature) {
            toast.error("Failed to send transaction");
            return;
        }
        console.log(`signature: ${signature}`);

        // Send the signed transaction to the backend
        dispatch(setSnackMsgAction("Transaction submitted. Please wait for confirmation."));
        const submitRes = await submitTransaction({
            signature,
            historyId,
        });
        dispatch(setSnackMsgAction(""));
        if (!submitRes.success) {
            toast.error(submitRes.msg);
            return;
        }
        toast.success("Transaction submitted successfully");
    };

    return (
        <Box
            pt={2}
            sx={{
                width: "100%",
                flex: 1,
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                gap: 2,
            }}
        >
            <Box
                display="flex"
                flexDirection="column"
                gap={2}
                sx={{ width: "100%", maxWidth: 1200 }}
            >
                <Box
                    mt={1}
                    sx={{
                        width: "100%",
                        height: 30,
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                    }}
                >
                    {settings?.mintingEnabled === false && (
                        <Typography variant="h6" color="warning">
                            SmartyPants minting is disabled.
                        </Typography>
                    )}
                </Box>
                {/* New Alert Message for Phantom Wallet Users */}
                <Box
                    mt={2}
                    sx={{
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        backgroundColor: "info.main",
                        padding: 1,
                        borderRadius: 1,
                        mx: 2
                    }}
                >
                    <Typography variant="body1" color="text.primary" textAlign="center">
                        Phantom Wallet users: While we wait for Phantom Wallet to approve our exchange, you will need to click through the warnings to use our exchange. We hope to have this resolved shortly.
                    </Typography>
                </Box>
                {/* End of New Alert Message */}
                {isCheckingSA && <Box mt={4}>
                    <Typography variant="h6" color="text.secondary" textAlign={"center"}>
                        Checking...
                    </Typography>
                </Box>}
                {!isCheckingSA && !isSAValid && <Box mt={4}>
                    <Typography variant="h6" color="error" textAlign={"center"}>
                        Signup Address is Invalid
                    </Typography>
                </Box>}
                {!isCheckingSA && isSAValid && <Grid container spacing={2} sx={{ width: "100%", mt: 0, p: 2 }}>
                    <Grid sx={{ mb: 4 }}>
                        <Typography
                            variant="h5"
                            color="text.secondary"
                            textAlign="center"
                            fontWeight={"bold"}
                        >
                            Welcome to the SMRTY ICO on Solana!
                        </Typography>
                        <Typography
                            variant="body1"
                            color="primary.main"
                            textAlign={"center"}
                            sx={{ mt: 1}}
                        >
                            <Link href="https://smartypants.exchange/videos/SMRTYvideos.html" target="_blank">
                                    If you need help click here for video instructions.
                            </Link>
                        </Typography>
                        <Typography
                            variant="body1"
                            color="text.secondary"
                            textAlign="center"
                            mt={4}
                        >
                            **Please refresh the page if you have any problems interacting
                            with the Sign up process. By pressing the “BUY SMRTY” button,
                            purchasing or selling SMRTY coins you are agreeing to the
                            following terms and conditions in the section of this page called
                            “TERMS & CONDITIONS”.
                        </Typography>
                    </Grid>

                    <Grid size={{ xs: 12, lg: 6 }}>
                        <TextField
                            label="Signup Address"
                            size="small"
                            variant="outlined"
                            error={!!errors.signupAddress}
                            helperText={errors.signupAddress}
                            sx={{ width: "100%" }}
                            value={signupAddress}
                            onChange={(e) => {setSignupAddress(e.target.value); setErrors({...errors, signupAddress: ""});}}
                            disabled={!isEditable}
                        />
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6, lg: 3 }}>
                        <NumberField
                            label={`USDC Amount (Min: ${settings?.minSignupAmount})`}
                            slotProps={{ htmlInput: { min: settings?.minSignupAmount } }}
                            sx={{ width: "100%" }}
                            value={amount}
                            onChange={(value) => {setAmount(value); setErrors({...errors, amount: ""});}}
                            helperText={errors.amount}
                        />
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6, lg: 3 }}>
                        <Button
                            variant="contained"
                            color="primary"
                            sx={{ width: "100%", height: "40px" }}
                            loading={isSigning}
                            loadingIndicator="Signing…"
                            onClick={handleSignUp}
                            disabled={!settings?.mintingEnabled}
                        >
                            BUY SMRTY
                        </Button>
                    </Grid>
                    <Grid size={{ xs: 12 }}>
                        <Typography
                            variant="body1"
                            textAlign={"left"}
                            fontSize={"13px"}
                            mt={-1}
                            sx={{}}
                        >
                            <Link
                                href="https://smartypants.exchange/jumptocommuntiy.htm"
                                target="_blank"
                                sx={{ mx: 0.5 }}
                                color="primary.main"
                            >
                                NOTE: If you do not have a sign-up address, click here to join
                                and obtain one by becoming a member of the SmartyPants
                                Community.
                            </Link>
                        </Typography>
                    </Grid>
                    <Grid size={{ xs: 12 }}>
                        <Section2 />
                    </Grid>
                </Grid>}
            </Box>
        </Box>
    );
}
